export enum ResourceType {
  OrganizationAudit = 'organization_audit',
  OrganizationAuthSettings = 'organization_auth_settings',
  OrganizationBillingSettings = 'organization_billing_settings',
  OrganizationDefaultLicensingPolicy = 'organization_default_licensing_policy',
  OrganizationDefaultQualityPolicy = 'organization_default_quality_policy',
  OrganizationDefaultSecurityPolicy = 'organization_default_security_policy',
  OrganizationDefaultSBOMPolicy = 'organization_default_sbom_policy',
  OrganizationGeneralSettings = 'organization_general_settings',
  OrganizationIntegrationSettings = 'organization_integration_settings',
  OrganizationLanguageSettings = 'organization_language_settings',
  OrganizationPortalSettings = 'organization_portal_settings',
  OrganizationPortalReports = 'organization_portal_reports',
  OrganizationReport = 'organization_report',

  ProjectAny = 'project_any',
  Project = 'project',

  ProjectLicensingIssueAny = 'project_licensing_issue_any',
  ProjectQualityIssueAny = 'project_quality_issue_any',
  ProjectSecurityIssueAny = 'project_security_issue_any',
  ProjectLicensingIssue = 'project_licensing_issue',
  ProjectQualityIssue = 'project_quality_issue',
  ProjectSecurityIssue = 'project_security_issue',

  ProjectLicensingPolicyAny = 'project_licensing_policy_any',
  ProjectQualityPolicyAny = 'project_quality_policy_any',
  ProjectSecurityPolicyAny = 'project_security_policy_any',
  ProjectLicensingPolicy = 'project_licensing_policy',
  ProjectQualityPolicy = 'project_quality_policy',
  ProjectSecurityPolicy = 'project_security_policy',
  ProjectSBOMPolicyAny = 'project_sbom_policy_any',
  ProjectSBOMPolicy = 'project_sbom_policy',

  ProjectLicensingReportPublicLink = 'project_licensing_report_public_link',
  ProjectLicensingReportPublicLinkAny = 'project_licensing_report_public_link_any',

  ReleaseGroupAny = 'release_group_any',
  ReleaseGroup = 'release_group',
  ReleaseGroupLicensingIssueAny = 'release_group_licensing_issue_any',
  ReleaseGroupQualityIssueAny = 'release_group_quality_issue_any',
  ReleaseGroupSecurityIssueAny = 'release_group_security_issue_any',
  ReleaseGroupLicensingIssue = 'release_group_licensing_issue',
  ReleaseGroupQualityIssue = 'release_group_quality_issue',
  ReleaseGroupSecurityIssue = 'release_group_security_issue',

  ReleaseGroupLicensingPolicy = 'release_group_licensing_policy',
  ReleaseGroupLicensingPolicyAny = 'release_group_licensing_policy_any',
  ReleaseGroupQualityPolicy = 'release_group_quality_policy',
  ReleaseGroupQualityPolicyAny = 'release_group_quality_policy_any',
  ReleaseGroupSecurityPolicy = 'release_group_security_policy',
  ReleaseGroupSecurityPolicyAny = 'release_group_security_policy_any',

  Role = 'role',

  TeamAny = 'team_any',
  Team = 'team',
  TeamProject = 'team_project',
  TeamProjectAny = 'team_project_any',
  TeamReleaseGroupAny = 'team_release_group_any',
  TeamReleaseGroup = 'team_release_group',
  TeamUserAny = 'team_user_any',
  TeamUser = 'team_user',
  TeamReport = 'team_report',

  LicensingPolicyAny = 'licensing_policy_any',
  LicensingPolicy = 'licensing_policy',
  SecurityPolicyAny = 'security_policy_any',
  SecurityPolicy = 'security_policy',
  QualityPolicyAny = 'quality_policy_any',
  QualityPolicy = 'quality_policy',
  SBOMPolicyAny = 'sbom_policy_any',
  SBOMPolicy = 'sbom_policy',

  User = 'user',
  UserAny = 'user_any',

  Label = 'label',

  IssueFilterLicensing = 'issue_filter_licensing',
  IssueFilterQuality = 'issue_filter_quality',
  IssueFilterSecurity = 'issue_filter_security',

  ProjectIssueFilterLicensing = 'project_issue_filter_licensing',
  ProjectIssueFilterQuality = 'project_issue_filter_quality',
  ProjectIssueFilterSecurity = 'project_issue_filter_security',

  ProjectIssueFilterLicensingAny = 'project_issue_filter_licensing_any',
  ProjectIssueFilterQualityAny = 'project_issue_filter_quality_any',
  ProjectIssueFilterSecurityAny = 'project_issue_filter_security_any',

  PackageLabel = 'package_label',

  ProjectReport = 'project_report',
  ProjectReportAny = 'project_report_any',

  ReleaseGroupReport = 'release_group_report',
  ReleaseGroupReportAny = 'release_group_report_any',
}

export enum Action {
  Add = 'add',
  Comment = 'comment',
  Create = 'create',
  Delete = 'delete',
  Edit = 'edit',
  Invite = 'invite',
  MakePublic = 'make_public',
  Manage = 'manage',
  Remove = 'remove',
  Resolve = 'resolve',
  Export = 'export',
  Set = 'set',
  SetLocal = 'set_local',
  View = 'view',
}

type BaseOrgPermission<TResource extends ResourceType, TAction extends Action> = {
  resourceType: TResource;
  action: TAction;
  projectId?: null;
  releaseGroupId?: null;
  teamId?: null;
};

type BaseProjectPermission<TResource extends ResourceType, TAction extends Action> = {
  resourceType: TResource;
  action: TAction;
  projectId: string;
  releaseGroupId?: null;
  teamId?: null;
};

type BaseTeamPermission<TResource extends ResourceType, TAction extends Action> = {
  resourceType: TResource;
  action: TAction;
  projectId?: null;
  releaseGroupId?: null;
  teamId: number;
};

type BaseReleaseGroupPermission<TResource extends ResourceType, TAction extends Action> = {
  resourceType: TResource;
  action: TAction;
  projectId?: null;
  releaseGroupId: number;
  teamId?: null;
};

/**
 * Permissions represented by ResourceType and Action combinations. All Permissions are
 * in the format of
 * ```
 * {
 *   resourceType: ResourceType;
 *   action: Action;
 *   projectId?: string | null;
 *   releaseGroupId?: number | null;
 *   teamId?: number | null;
 * }
 * ```
 */
export type Permission =
  /*
   *
   * Issue Filter Permissions
   *
   */
  | BaseOrgPermission<ResourceType.IssueFilterLicensing, Action.Delete | Action.Create | Action.Edit>
  | BaseOrgPermission<ResourceType.IssueFilterQuality, Action.Delete | Action.Create | Action.Edit>
  | BaseOrgPermission<ResourceType.IssueFilterSecurity, Action.Delete | Action.Create | Action.Edit>
  /*
   *
   * Organization Settings Permissions
   *
   */
  | BaseOrgPermission<ResourceType.OrganizationAudit, Action.View>
  | BaseOrgPermission<ResourceType.OrganizationAuthSettings, Action.Edit>
  | BaseOrgPermission<ResourceType.OrganizationBillingSettings, Action.Edit>
  | BaseOrgPermission<ResourceType.OrganizationDefaultLicensingPolicy, Action.Set>
  | BaseOrgPermission<ResourceType.OrganizationDefaultQualityPolicy, Action.Set>
  | BaseOrgPermission<ResourceType.OrganizationDefaultSBOMPolicy, Action.Set>
  | BaseOrgPermission<ResourceType.OrganizationDefaultSecurityPolicy, Action.Set>
  | BaseOrgPermission<ResourceType.OrganizationGeneralSettings, Action.Edit | Action.View>
  | BaseOrgPermission<ResourceType.OrganizationIntegrationSettings, Action.Edit>
  | BaseOrgPermission<ResourceType.OrganizationLanguageSettings, Action.Edit | Action.View>
  | BaseOrgPermission<ResourceType.OrganizationPortalSettings, Action.Edit | Action.View>
  /*
   *
   * Organization Report Permissions
   *
   */
  | BaseOrgPermission<ResourceType.OrganizationReport, Action.Create>
  | BaseOrgPermission<ResourceType.OrganizationPortalReports, Action.Manage>
  /*
   *
   * Project/Package Label Permissions
   *
   */
  | BaseOrgPermission<ResourceType.Label, Action.Delete | Action.Create>
  | BaseOrgPermission<ResourceType.PackageLabel, Action.Create | Action.Delete | Action.Set>
  | BaseTeamPermission<ResourceType.PackageLabel, Action.SetLocal>
  /*
   *
   * Project CRUD Permissions
   *
   */
  | BaseProjectPermission<
      ResourceType.Project,
      Action.Create | Action.Delete | Action.Edit | Action.MakePublic | Action.View
    >
  | BaseOrgPermission<ResourceType.Project, Action.Create>
  | BaseOrgPermission<ResourceType.ProjectAny, Action.View | Action.Edit | Action.Delete | Action.MakePublic>
  /*
   *
   * Project Issue Filter Permissions
   *
   */
  | BaseOrgPermission<ResourceType.ProjectIssueFilterLicensing, Action.Edit>
  | BaseOrgPermission<ResourceType.ProjectIssueFilterLicensingAny, Action.Edit>
  | BaseOrgPermission<ResourceType.ProjectIssueFilterQuality, Action.Edit>
  | BaseOrgPermission<ResourceType.ProjectIssueFilterQualityAny, Action.Edit>
  | BaseOrgPermission<ResourceType.ProjectIssueFilterSecurity, Action.Edit>
  | BaseOrgPermission<ResourceType.ProjectIssueFilterSecurityAny, Action.Edit>
  /*
   *
   * Project Issue Permissions
   *
   */
  | BaseProjectPermission<
      ResourceType.ProjectLicensingIssue,
      Action.Comment | Action.Resolve | Action.Export | Action.View
    >
  | BaseOrgPermission<
      ResourceType.ProjectLicensingIssueAny,
      Action.View | Action.Comment | Action.Resolve | Action.Export
    >
    | BaseProjectPermission<
    ResourceType.ProjectQualityIssue,
    Action.Comment | Action.Resolve | Action.Export | Action.View
  >
| BaseOrgPermission<
    ResourceType.ProjectQualityIssueAny,
    Action.View | Action.Comment | Action.Resolve | Action.Export
  >
  | BaseProjectPermission<
      ResourceType.ProjectSecurityIssue,
      Action.Comment | Action.Resolve | Action.Export | Action.View
    >
  | BaseOrgPermission<
      ResourceType.ProjectSecurityIssueAny,
      Action.Comment | Action.Resolve | Action.Export | Action.View
    >
  /*
   *
   * Project Policy Permissions
   *
   */
  | BaseProjectPermission<ResourceType.ProjectLicensingPolicy, Action.Set>
  | BaseOrgPermission<ResourceType.ProjectLicensingPolicyAny, Action.Set>
  | BaseProjectPermission<ResourceType.ProjectQualityPolicy, Action.Set>
  | BaseOrgPermission<ResourceType.ProjectQualityPolicyAny, Action.Set>
  | BaseProjectPermission<
      ResourceType.ProjectSBOMPolicy,
      Action.Create | Action.Delete | Action.Edit | Action.Set | Action.View
    >
  | BaseOrgPermission<
      ResourceType.ProjectSBOMPolicyAny,
      Action.Create | Action.Delete | Action.Edit | Action.Set | Action.View
    >
    | BaseOrgPermission<ResourceType.ProjectSecurityPolicy, Action.Set>
    | BaseOrgPermission<ResourceType.ProjectSecurityPolicyAny, Action.Set>
  /*
   *
   * Project Report Permissions
   *
   */
  | BaseProjectPermission<ResourceType.ProjectLicensingReportPublicLink, Action.Create>
  | BaseOrgPermission<ResourceType.ProjectLicensingReportPublicLinkAny, Action.Create>
  /*
   *
   * Policy Permissions
   *
   */
  | BaseOrgPermission<ResourceType.LicensingPolicy, Action.Create>
  | BaseOrgPermission<ResourceType.LicensingPolicyAny, Action.Delete | Action.Edit | Action.View>
  | BaseOrgPermission<ResourceType.SecurityPolicy, Action.Create>
  | BaseOrgPermission<ResourceType.SecurityPolicyAny, Action.Delete | Action.Edit | Action.View>
  | BaseOrgPermission<ResourceType.QualityPolicy, Action.Create>
  | BaseOrgPermission<ResourceType.QualityPolicyAny, Action.Delete | Action.Edit | Action.View>
  | BaseOrgPermission<ResourceType.SBOMPolicy, Action.Create | Action.Delete | Action.Edit | Action.Set | Action.View>
  | BaseOrgPermission<ResourceType.SBOMPolicyAny, Action.View | Action.Delete | Action.View>
  /*
   *
   * Release Group CRUD Permissions
   *
   */
  | BaseOrgPermission<ResourceType.ReleaseGroup, Action.Create>
  | BaseReleaseGroupPermission<ResourceType.ReleaseGroup, Action.View | Action.Edit | Action.Create | Action.Delete>
  | BaseOrgPermission<ResourceType.ReleaseGroupAny, Action.View | Action.Delete | Action.Edit>
  /*
   *
   * Release Group Issue Permissions
   *
   */
  | BaseReleaseGroupPermission<
      ResourceType.ReleaseGroupLicensingIssue,
      Action.Comment | Action.Resolve | Action.Export | Action.View
    >
  | BaseOrgPermission<
      ResourceType.ReleaseGroupLicensingIssueAny,
      Action.Comment | Action.Resolve | Action.Export | Action.View
    >
    | BaseReleaseGroupPermission<
    ResourceType.ReleaseGroupQualityIssue,
    Action.Comment | Action.Resolve | Action.Export | Action.View
  >
  | BaseOrgPermission<
    ResourceType.ReleaseGroupQualityIssueAny,
    Action.Comment | Action.Resolve | Action.Export | Action.View
  >
  | BaseReleaseGroupPermission<
      ResourceType.ReleaseGroupSecurityIssue,
      Action.Comment | Action.Resolve | Action.Export | Action.View
    >
  | BaseOrgPermission<
      ResourceType.ReleaseGroupSecurityIssueAny,
      Action.Comment | Action.Resolve | Action.Export | Action.View
    >
  /*
   *
   * Release Group Policy Permissions
   *
   */
  | BaseOrgPermission<ResourceType.ReleaseGroupLicensingPolicy, Action.Set>
  | BaseOrgPermission<ResourceType.ReleaseGroupLicensingPolicyAny, Action.Set>
  | BaseOrgPermission<ResourceType.ReleaseGroupQualityPolicy, Action.Set>
  | BaseOrgPermission<ResourceType.ReleaseGroupQualityPolicyAny, Action.Set>
  | BaseOrgPermission<ResourceType.ReleaseGroupSecurityPolicy, Action.Set>
  | BaseOrgPermission<ResourceType.ReleaseGroupSecurityPolicyAny, Action.Set>
  /*
   *
   * Team Permissions
   *
   */
  | BaseOrgPermission<ResourceType.Team, Action.Create>
  | BaseTeamPermission<ResourceType.Team, Action.Edit | Action.View>
  | BaseOrgPermission<ResourceType.TeamAny, Action.View | Action.Delete | Action.Edit>
  | BaseTeamPermission<ResourceType.TeamProject, Action.Add | Action.Remove>
  | BaseOrgPermission<ResourceType.TeamProjectAny, Action.Add | Action.Remove>
  | BaseTeamPermission<ResourceType.TeamReleaseGroup, Action.Add | Action.Remove>
  | BaseOrgPermission<ResourceType.TeamReleaseGroupAny, Action.Add | Action.Remove>
  | BaseOrgPermission<ResourceType.TeamReport, Action.Create>
  | BaseTeamPermission<ResourceType.TeamUser, Action.Edit>
  | BaseOrgPermission<ResourceType.TeamUserAny, Action.Edit>
  /*
   *
   * User Management Permissions
   *
   */
  | BaseOrgPermission<ResourceType.Role, Action.Manage>
  | BaseOrgPermission<ResourceType.User, Action.Invite>
  | BaseOrgPermission<ResourceType.UserAny, Action.View | Action.Remove>
  /*
   *
   * Generate reports
   *
   */
  | BaseProjectPermission<ResourceType.ProjectReport, Action.Create>
  | BaseOrgPermission<ResourceType.ProjectReportAny, Action.Create>
  | BaseReleaseGroupPermission<ResourceType.ReleaseGroupReport, Action.Create>
  | BaseOrgPermission<ResourceType.ReleaseGroupReportAny, Action.Create>
  ;
